import { StripeError } from '@stripe/stripe-js'
import { APP_NAME, RequestMethod } from '~/shared/constants/commonConstants'
import { ApiService } from '~/shared/services/api'
import { IRequestOptions, IResponseResult } from '~/shared/types/apiTypes'
import {
  IPurchaseResponseRaw,
  IPurchaseRetrySubscriptionResponseRaw,
  ISecondaryResponseRaw,
} from '~/shared/types/paymentTypes'

export class StripePaymentApi {
  private readonly baseUrl = 'v2/purchases'
  private api: ApiService

  constructor(api: ApiService) {
    this.api = api
  }

  createSubscription({
    uuid,
    planId,
    trialPeriodDays,
    promoCode,
    cohort,
  }: {
    uuid: string
    promoCode?: string
    planId: number
    trialPeriodDays: number
    cohort: string
  }): Promise<IResponseResult<IPurchaseResponseRaw>> {
    const options: IRequestOptions = {
      method: RequestMethod.POST,
      body: {
        plan_id: planId,
        promo_code: promoCode,
        trial_duration: trialPeriodDays,
        cohort,
      },
    }

    return this.api.makeRequest<IPurchaseResponseRaw>(
      `${this.baseUrl}/stripe/${uuid}/${APP_NAME}`,
      options,
    )
  }

  retryPayment({
    uuid,
    stripeError,
  }: {
    uuid: string
    stripeError: StripeError
  }): Promise<IResponseResult<IPurchaseRetrySubscriptionResponseRaw>> {
    const options: IRequestOptions = {
      method: RequestMethod.POST,
      body: {
        payment_error: stripeError,
      },
    }
    return this.api.makeRequest<IPurchaseRetrySubscriptionResponseRaw>(
      `${this.baseUrl}/stripe/${uuid}/${APP_NAME}/retry`,
      options,
    )
  }

  makeUpsell({
    uuid,
    planId,
    productId,
    promoCode,
  }: {
    uuid: string
    planId: number
    productId: string
    promoCode?: string
  }): Promise<IResponseResult<IPurchaseResponseRaw>> {
    const options: IRequestOptions = {
      method: RequestMethod.POST,
      body: {
        plan_id: planId,
        description: productId,
        promo_code: promoCode,
      },
    }

    return this.api.makeRequest<IPurchaseResponseRaw>(
      `${this.baseUrl}/stripe/${uuid}/${APP_NAME}/upsell`,
      options,
    )
  }

  makeSecondary({
    uuid,
    planId,
    productId,
    promoCode,
  }: {
    uuid: string
    planId: number
    productId: string
    promoCode?: string
  }): Promise<IResponseResult<ISecondaryResponseRaw>> {
    const options: IRequestOptions = {
      method: RequestMethod.POST,
      body: {
        plan_id: planId,
        description: productId,
        promo_code: promoCode,
      },
    }

    return this.api.makeRequest<ISecondaryResponseRaw>(
      `${this.baseUrl}/${uuid}/${APP_NAME}/secondary`,
      options,
    )
  }
}
